import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Key, Trash2, Plus, ShieldCheck, FolderLock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  listSecureKeys,
  setSecureKey,
  deleteSecureKey,
  migrateKeysToVault,
  getSecureStorageConfig,
} from '@/lib/api';

interface SecureKeysPanelProps {
  codebaseId: string;
}

export function SecureKeysPanel({ codebaseId }: SecureKeysPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [migrateResult, setMigrateResult] = useState<{
    moved: number;
    failed: number;
    operator: string[];
  } | null>(null);

  const { data: keys } = useQuery({
    queryKey: ['secureKeys', codebaseId],
    queryFn: () => listSecureKeys(codebaseId),
  });

  const { data: storageConfig } = useQuery({
    queryKey: ['secureStorageConfig'],
    queryFn: getSecureStorageConfig,
  });

  const setMutation = useMutation({
    mutationFn: (data: { key: string; value: string }) => setSecureKey(codebaseId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['secureKeys', codebaseId] });
      setNewKey('');
      setNewValue('');
      setShowAddForm(false);
      setMutationError(null);
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteSecureKey(codebaseId, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['secureKeys', codebaseId] });
      setMutationError(null);
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  const migrateMutation = useMutation({
    mutationFn: () => migrateKeysToVault(codebaseId),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: ['secureKeys', codebaseId] });
      setMigrateResult({
        moved: result.movedKeys.length,
        failed: result.failedKeys.length,
        operator: result.operatorKeys.map(k => `${k.file}:${k.key}`),
      });
      setMutationError(null);
    },
    onError: (err: Error) => {
      setMutationError(err.message);
    },
  });

  function handleAdd(e: React.FormEvent): void {
    e.preventDefault();
    if (newKey.trim() && newValue !== '') {
      setMutation.mutate({ key: newKey.trim(), value: newValue });
    }
  }

  function handleDelete(key: string): void {
    void confirm({
      title: `Delete secure key "${key}"?`,
      description: 'This permanently deletes the encrypted secret from the vault.',
      risk: 'critical',
      confirmPhrase: key,
      confirmLabel: 'Delete key',
    }).then(ok => {
      if (ok) deleteMutation.mutate(key);
    });
  }

  const keyList = keys ?? [];

  return (
    <>
      <div className="mt-2 pl-2 border-l border-border space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-text-secondary" />
          <span className="text-xs font-medium text-text-primary">Secure Keys</span>
          {storageConfig?.path && (
            <Badge variant="outline" className="text-[10px] gap-1">
              <FolderLock className="h-2.5 w-2.5" />
              {storageConfig.path}
            </Badge>
          )}
        </div>

        {/* Error display */}
        {mutationError && (
          <div className="rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
            {mutationError}
          </div>
        )}

        {/* Key list */}
        {keyList.length === 0 ? (
          <div className="text-xs text-text-tertiary">No secure keys stored.</div>
        ) : (
          <div className="space-y-1">
            {keyList.map(key => (
              <div key={key} className="flex items-center gap-2 text-xs">
                <Key className="h-3 w-3 text-text-tertiary" />
                <span className="font-mono text-text-primary truncate flex-1">{key}</span>
                <span className="text-text-tertiary">= ******</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-xs text-error"
                  onClick={() => {
                    handleDelete(key);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add form (toggle) */}
        {showAddForm ? (
          <form onSubmit={handleAdd} className="space-y-1">
            <div className="flex gap-1">
              <Input
                value={newKey}
                onChange={e => {
                  setNewKey(e.target.value);
                }}
                placeholder="KEY_NAME"
                className="flex-1 h-7 text-xs font-mono"
                autoFocus
              />
              <Input
                value={newValue}
                onChange={e => {
                  setNewValue(e.target.value);
                }}
                placeholder="secret value"
                type="password"
                className="flex-1 h-7 text-xs"
              />
            </div>
            <div className="flex gap-1">
              <Button
                type="submit"
                size="sm"
                className="h-7 text-xs"
                disabled={setMutation.isPending}
              >
                <Plus className="h-3 w-3 mr-1" />
                Save Key
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setShowAddForm(false);
                  setNewKey('');
                  setNewValue('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              setShowAddForm(true);
            }}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Key
          </Button>
        )}

        {/* Migrate from .env */}
        <div className="border-t border-border pt-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              migrateMutation.mutate();
            }}
            disabled={migrateMutation.isPending}
          >
            {migrateMutation.isPending ? 'Migrating...' : 'Migrate from .env'}
          </Button>
          {migrateResult && (
            <div className="mt-1 text-xs text-text-secondary">
              Migrated {migrateResult.moved} key{migrateResult.moved !== 1 ? 's' : ''}
              {migrateResult.failed > 0 && (
                <span className="text-error">, {migrateResult.failed} failed</span>
              )}
            </div>
          )}
          {migrateResult && migrateResult.operator.length > 0 && (
            <div className="mt-1 rounded border border-error/50 bg-error/10 p-2 text-xs text-error">
              {migrateResult.operator.length} operator/infra key
              {migrateResult.operator.length !== 1 ? 's' : ''} left in .env and{' '}
              <strong>not stored here</strong> — these belong in MOS HashiCorp Vault. Route them
              there and remove them from .env: {migrateResult.operator.join(', ')}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="text-[10px] text-text-tertiary">
          Values are encrypted and stored outside the repository.
        </div>
      </div>
      {confirmDialog}
    </>
  );
}
